import { useQuery } from '@tanstack/react-query';
import { payslipService } from '@/services/payslips/payslipService';
import { validationService } from '@/services/payslips/validationService';
import { payslipKeys } from './queryKeys';
import type { CompensationRule } from '@/services/compensation/types';

export const usePayslips = (profileId: string | undefined) => {
  return useQuery({
    queryKey: payslipKeys.list(profileId || ''),
    queryFn: () => payslipService.getPayslips(profileId!),
    enabled: !!profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error.message.includes('JWT')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const usePayslipDetails = (payslipId: string | undefined) => {
  return useQuery({
    queryKey: payslipKeys.detail(payslipId || ''),
    queryFn: () => payslipService.getPayslipDetails(payslipId!),
    enabled: !!payslipId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error.message.includes('JWT')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const usePayslipLineItems = (payslipId: string | undefined) => {
  return useQuery({
    queryKey: payslipKeys.lineItems(payslipId || ''),
    queryFn: () => payslipService.getPayslipLineItems(payslipId!),
    enabled: !!payslipId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useLineItemBreakdown = (payslipId: string | undefined) => {
  return useQuery({
    queryKey: payslipKeys.breakdown(payslipId || ''),
    queryFn: () => payslipService.getLineItemBreakdown(payslipId!),
    enabled: !!payslipId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCompensationValidation = (
  companyId: string | undefined,
  changes: CompensationRule[]
) => {
  return useQuery({
    queryKey: payslipKeys.validation(companyId || '', changes),
    queryFn: () => validationService.validateCompensationChanges(companyId!, changes),
    enabled: !!companyId && changes.length > 0,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const usePayslipValidation = (
  profileId: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined
) => {
  return useQuery({
    queryKey: payslipKeys.payslipValidation(profileId || '', startDate || '', endDate || ''),
    queryFn: () => validationService.validatePayslipGeneration(profileId!, startDate!, endDate!),
    enabled: !!profileId && !!startDate && !!endDate,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 2 * 60 * 1000, // 2 minutes
  });
};
